import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  AppBar, Toolbar, Typography, IconButton, Box, Button, TextField, Chip,
  CircularProgress, Snackbar, Tooltip, Avatar, Grid, Divider,
} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import Pagination from '@material-ui/lab/Pagination';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import AddIcon from '@material-ui/icons/Add';
import SearchIcon from '@material-ui/icons/Search';
import EmailIcon from '@material-ui/icons/Email';
import LinkIcon from '@material-ui/icons/Link';
import RefreshIcon from '@material-ui/icons/Refresh';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import PhoneIcon from '@material-ui/icons/Phone';
import SupervisorAccountIcon from '@material-ui/icons/SupervisorAccount';
import GroupWorkIcon from '@material-ui/icons/GroupWork';
import ClearIcon from '@material-ui/icons/Clear';
import EventNoteIcon from '@material-ui/icons/EventNote';
import { useHistory } from 'react-router-dom';

import { EvaluationCategoryData, prospectService, userService } from '../../../services';
import RouteConstants from '../../../common/RouteConstants';
import ConfirmationDialog from '../../../sharedComponents/dialog/ConfirmationDialog';

const useStyles = makeStyles((theme) => ({
  appbarBackground: {
    backgroundColor: '#fff',
    color: 'red',
  },
  title: {
    flexGrow: 1,
  },
  headerBtn: {
    marginLeft: '5px',
    marginRight: '5px',
    border: '1px solid #1d428a',
    borderRadius: '4px',
    padding: '6px 8px',
    fontSize: '0.875rem',
    color: '#fff',
    background: '#1d428a',
    '&:hover': { color: '#fff', background: '#1d428a' },
    '&:focus': { color: '#fff', background: '#1d428a' },
    '& svg': { marginRight: '5px' },
  },
  intro: { color: '#555', marginBottom: theme.spacing(2), fontSize: 13 },
  prospectCard: {
    backgroundColor: '#fff',
    border: '1px solid #dbe4f3',
    borderLeft: '4px solid #1d428a',
    borderRadius: 6,
    padding: theme.spacing(1, 1.5),
    marginBottom: theme.spacing(1),
    boxShadow: '0 1px 2px rgba(29, 66, 138, 0.06)',
  },
  prospectBadge: {
    display: 'inline-block',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: '#1d428a',
    backgroundColor: '#e8eef9',
    borderRadius: 3,
    padding: '1px 6px',
    marginBottom: 2,
  },
  evalChip: {
    backgroundColor: '#fff4e0',
    color: '#b26a00',
    border: '1px solid #f2d19a',
    fontWeight: 600,
  },
  suggestion: {
    backgroundColor: '#eef6ee',
    border: '1px solid #c6e6c6',
    borderRadius: 4,
    padding: theme.spacing(1),
    marginTop: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  matchArea: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: '#f2f8f8',
    border: '1px solid #cfe6e3',
    borderLeft: '3px solid #0f766e',
    borderRadius: 6,
  },
  matchAreaTitle: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: '#0f766e',
    marginBottom: theme.spacing(0.5),
  },
  resultsList: {
    maxHeight: 320,
    overflowY: 'auto',
    marginTop: theme.spacing(0.5),
    borderTop: '1px solid #cfe6e3',
  },
  matchRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    borderBottom: '1px solid #dcebe9',
    borderLeft: '3px solid transparent',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
    '&:last-child': { borderBottom: 'none' },
    '&:hover': { backgroundColor: '#e6f3f1' },
  },
  matchRowSelected: {
    backgroundColor: '#dcefec',
    borderLeft: '3px solid #0f766e',
    '&:hover': { backgroundColor: '#d3eae6' },
  },
  matchRowInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    minWidth: 0,
  },
  officialAvatar: {
    width: 44,
    height: 44,
    backgroundColor: '#0f766e',
    color: '#fff',
    fontSize: 15,
    fontWeight: 600,
  },
  officialName: { fontWeight: 600, color: '#0f5c56' },
  matchScoreBadge: {
    display: 'inline-block',
    marginLeft: theme.spacing(1),
    padding: '1px 6px',
    borderRadius: 10,
    fontSize: '0.68rem',
    fontWeight: 700,
    verticalAlign: 'middle',
  },
  searchInputs: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1),
  },
  nameField: { flex: '1 1 130px', backgroundColor: '#fff' },
  emailField: { flex: '1 1 200px', backgroundColor: '#f7f9fc' },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(6, 2),
    color: '#777',
  },
  rightPanel: {
    position: 'sticky',
    top: theme.spacing(2),
    backgroundColor: '#fff',
    border: '1px solid #dbe4f3',
    borderRadius: 8,
    minHeight: 320,
    boxShadow: '0 1px 3px rgba(29, 66, 138, 0.08)',
    overflow: 'hidden',
  },
  rightPanelHeader: {
    backgroundColor: '#0f766e',
    color: '#fff',
    padding: theme.spacing(1, 1.5),
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  detailBody: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  detailAvatar: {
    width: 128,
    height: 128,
    backgroundColor: '#0f766e',
    color: '#fff',
    fontSize: 40,
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
    boxShadow: '0 2px 8px rgba(15, 118, 110, 0.25)',
  },
  detailName: { fontWeight: 700, color: '#0f5c56', fontSize: 18 },
  detailMetaRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    color: '#555',
    fontSize: 13,
    marginTop: theme.spacing(0.5),
  },
  detailInfoList: {
    width: '100%',
    marginTop: theme.spacing(1.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  detailInfoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    textAlign: 'left',
    color: '#333',
    fontSize: 13,
  },
  detailInfoLabel: {
    fontWeight: 600,
    color: '#0f5c56',
    minWidth: 96,
  },
  detailInfoValue: {
    color: '#444',
    wordBreak: 'break-word',
  },
  detailTagChip: {
    margin: 2,
    backgroundColor: '#e8eef9',
    color: '#1d428a',
    fontWeight: 600,
  },
  evalSummaryBox: {
    width: '100%',
    marginTop: theme.spacing(1),
    textAlign: 'left',
  },
  evalSummaryHeading: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: '#0f5c56',
    marginBottom: theme.spacing(0.75),
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  evalSummaryItem: {
    border: '1px solid #dbe4f3',
    borderLeft: '3px solid #1d428a',
    borderRadius: 6,
    padding: theme.spacing(0.75, 1),
    marginBottom: theme.spacing(0.75),
    backgroundColor: '#f8faff',
  },
  evalSummaryEvent: {
    fontWeight: 600,
    color: '#1d428a',
    fontSize: 13,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  evalSummaryMeta: {
    marginTop: 2,
    color: '#5a6472',
    fontSize: 12,
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  evalStatusChip: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    borderRadius: 3,
    padding: '1px 6px',
    whiteSpace: 'nowrap',
  },
  evalStatusDraft: {
    color: '#b26a00',
    backgroundColor: '#fff4e0',
    border: '1px solid #f2d19a',
  },
  evalStatusSubmitted: {
    color: '#16a34a',
    backgroundColor: '#dcfce7',
    border: '1px solid #bbf7d0',
  },
  detailPlaceholder: {
    padding: theme.spacing(6, 2),
    textAlign: 'center',
    color: '#9aa2ad',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const PAGE_SIZE = 10;

const MapOfficialsPage = () => {
  const classes = useStyles();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [prospects, setProspects] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  // Per-prospect manual match state keyed by evalProspectUID.
  const [matchState, setMatchState] = useState({});
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [confirm, setConfirm] = useState({ open: false, prospect: null, official: null });
  const [mapping, setMapping] = useState(false);
  // Currently selected match shown in the right-hand detail panel.
  const [selectedDetail, setSelectedDetail] = useState(null); // { prospect, official }
  // Extended profile details (phone, primary scout, pool) fetched per official
  // and cached by officialUID so the detail pane can show more than the basics.
  const [officialProfiles, setOfficialProfiles] = useState({});
  const [profileLoading, setProfileLoading] = useState(false);

  const loadProspects = useCallback(async () => {
    setLoading(true);
    setSearch('');
    setPage(1);
    try {
      const data = await EvaluationCategoryData.getUnmappedProspects();
      // Only show prospects that have at least one evaluation attached.
      const filtered = Array.isArray(data)
        ? data.filter((p) => (p.evaluationCount || 0) > 0)
        : [];
      setProspects(filtered);
      setMatchState({});
      setSelectedDetail(null);
    } catch (err) {
      setToast({ open: true, message: err?.message || 'Failed to load prospects.', severity: 'error' });
      setProspects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProspects();
  }, [loadProspects]);

  const updateMatchState = (uid, patch) => {
    setMatchState((prev) => ({ ...prev, [uid]: { ...(prev[uid] || {}), ...patch } }));
  };

  const getFirst = (prospect) => {
    const s = matchState[prospect.evalProspectUID] || {};
    return s.firstName !== undefined ? s.firstName : (prospect.firstName || '');
  };

  const getLast = (prospect) => {
    const s = matchState[prospect.evalProspectUID] || {};
    return s.lastName !== undefined ? s.lastName : (prospect.lastName || '');
  };

  // Fetch head-shots for the matched officials and merge their URLs into the
  // per-prospect result list so each match can display a profile picture.
  const attachHeadShots = async (uid, results) => {
    const officialUIDs = (results || [])
      .map((official) => official.officialUID)
      .filter(Boolean);
    if (officialUIDs.length === 0) return;
    try {
      const headShots = await prospectService.getProfileHeadShots({ OfficialUIDList: officialUIDs });
      if (!Array.isArray(headShots) || headShots.length === 0) return;
      const headShotByUID = headShots.reduce((map, item) => {
        if (item && item.officialUID) {
          map[String(item.officialUID).toLowerCase()] = item.headShot;
        }
        return map;
      }, {});
      setMatchState((prev) => {
        const current = prev[uid];
        if (!current || !Array.isArray(current.results)) return prev;
        const nextResults = current.results.map((official) => ({
          ...official,
          headShot: headShotByUID[String(official.officialUID).toLowerCase()] || official.headShot,
        }));
        return { ...prev, [uid]: { ...current, results: nextResults } };
      });
    } catch (err) {
      // Head-shots are a visual enhancement only; ignore failures silently.
      console.warn('Failed to load official head-shots:', err);
    }
  };

  // Fetch the extended profile (phone, primary scout, pool) for a real official
  // so the detail pane can show more than the name/head-shot returned by the
  // name-match search. Results are cached by officialUID to avoid re-fetching.
  const fetchOfficialProfile = async (official) => {
    if (!official || !official.officialUID) return;
    const key = String(official.officialUID).toLowerCase();
    if (officialProfiles[key]) return;
    // A username is required to look up the full profile; name-only prospects
    // won't have one, so there is nothing more to fetch.
    if (!official.username) return;
    setProfileLoading(true);
    try {
      const profile = await userService.getContactDetails(official.username, false);
      setOfficialProfiles((prev) => ({ ...prev, [key]: profile || {} }));
    } catch (err) {
      // Extended details are optional; keep the pane usable on failure.
      console.warn('Failed to load official profile details:', err);
      setOfficialProfiles((prev) => ({ ...prev, [key]: {} }));
    } finally {
      setProfileLoading(false);
    }
  };

  const handleSelectOfficial = (prospect, official) => {
    setSelectedDetail({ prospect, official });
    fetchOfficialProfile(official);
  };

  const handleSearchOfficials = async (prospect) => {
    const uid = prospect.evalProspectUID;
    const firstName = getFirst(prospect).trim();
    const lastName = getLast(prospect).trim();
    if (!firstName && !lastName) {
      setToast({ open: true, message: 'Enter a first or last name to search.', severity: 'warning' });
      return;
    }
    updateMatchState(uid, { searching: true });
    try {
      const data = await EvaluationCategoryData.matchOfficialsByName(firstName, lastName);
      const results = Array.isArray(data) ? data : [];
      updateMatchState(uid, { results, searched: true, searching: false });
      attachHeadShots(uid, results);
    } catch (err) {
      updateMatchState(uid, { searching: false });
      setToast({ open: true, message: err?.message || 'Search failed.', severity: 'error' });
    }
  };

  const requestMap = (prospect, official) => {
    setConfirm({ open: true, prospect, official });
  };

  // Clear the official search results for a single prospect, resetting its name
  // inputs back to the prospect's original values and closing the detail pane
  // if it was showing an official matched for this prospect.
  const handleClearSearch = (prospect) => {
    const uid = prospect.evalProspectUID;
    setMatchState((prev) => {
      const next = { ...prev };
      delete next[uid];
      return next;
    });
    setSelectedDetail((prev) => (
      prev?.prospect?.evalProspectUID === uid ? null : prev
    ));
  };

  const handleConfirmMap = async (result) => {
    if (!result) {
      setConfirm({ open: false, prospect: null, official: null });
      return;
    }
    const { prospect, official } = confirm;
    setMapping(true);
    try {
      const res = await EvaluationCategoryData.mapProspectToOfficial(
        prospect.evalProspectUID,
        official.officialUID,
      );
      const transferred = res?.transferredCount ?? 0;
      setProspects((prev) => prev.filter((p) => p.evalProspectUID !== prospect.evalProspectUID));
      // Clear the detail panel if it was showing the just-mapped prospect.
      setSelectedDetail((prev) => (
        prev?.prospect?.evalProspectUID === prospect.evalProspectUID ? null : prev
      ));
      setToast({
        open: true,
        message: `Mapped ${prospect.firstName || ''} ${prospect.lastName || ''} to ${official.firstName || ''} ${official.lastName || ''}. ${transferred} evaluation(s) transferred.`,
        severity: 'success',
      });
    } catch (err) {
      setToast({ open: true, message: err?.message || 'Mapping failed.', severity: 'error' });
    } finally {
      setMapping(false);
      setConfirm({ open: false, prospect: null, official: null });
    }
  };

  const fullName = (first, last) => `${(first || '').trim()} ${(last || '').trim()}`.trim() || '(no name)';

  const getInitials = (first, last) => {
    const f = (first || '').trim();
    const l = (last || '').trim();
    const initials = `${f.charAt(0)}${l.charAt(0)}`.toUpperCase();
    return initials || '?';
  };

  // Parse the compact per-evaluation summary embedded in the prospect payload.
  // The backend returns a JSON array string (FOR JSON PATH); guard against
  // missing/invalid data so a bad row never breaks the detail pane.
  const parseEvaluations = (prospect) => {
    if (!prospect || !prospect.evaluationsJson) return [];
    try {
      const parsed = JSON.parse(prospect.evaluationsJson);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      console.warn('Failed to parse prospect evaluations summary:', err);
      return [];
    }
  };

  const formatEvalDate = (value) => {
    if (!value) return 'No date';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'No date';
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getMatchScoreStyle = (score) => {
    if (score >= 85) return { backgroundColor: '#dcfce7', color: '#16a34a' };
    if (score >= 65) return { backgroundColor: '#fef9c3', color: '#ca8a04' };
    return { backgroundColor: '#fee2e2', color: '#dc2626' };
  };

  const searchTerm = search.trim().toLowerCase();
  const filteredProspects = searchTerm
    ? prospects.filter((p) => {
      const name = `${p.firstName || ''} ${p.lastName || ''}`.toLowerCase();
      const email = (p.email || '').toLowerCase();
      return name.includes(searchTerm) || email.includes(searchTerm);
    })
    : prospects;

  const pageCount = Math.ceil(filteredProspects.length / PAGE_SIZE) || 1;
  const currentPage = Math.min(page, pageCount);
  const pagedProspects = filteredProspects.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  // The `selectedDetail.official` captured on click is a snapshot that may
  // predate the asynchronous head-shot attachment. Re-resolve it against the
  // current matchState so the detail pane always reflects the latest data
  // (including the head-shot) shown in the list.
  const selectedOfficial = (() => {
    if (!selectedDetail || !selectedDetail.official || !selectedDetail.prospect) {
      return selectedDetail ? selectedDetail.official : null;
    }
    const uid = selectedDetail.prospect.evalProspectUID;
    const liveResults = (matchState[uid] && matchState[uid].results) || [];
    const live = liveResults.find(
      (o) => o.officialUID === selectedDetail.official.officialUID,
    );
    return live || selectedDetail.official;
  })();

  // Extended profile (phone, primary scout, pool) for the selected official,
  // if it has been fetched and cached.
  const selectedProfile = selectedOfficial && selectedOfficial.officialUID
    ? officialProfiles[String(selectedOfficial.officialUID).toLowerCase()]
    : null;

  return (
    <>
      <AppBar position="static" className={classes.appbarBackground}>
        <Toolbar>
          <Typography variant="h6" className={classes.title}>
            Map Officials
          </Typography>
          <Tooltip title="Refresh">
            <span>
              <IconButton edge="end" aria-label="Refresh" onClick={loadProspects} disabled={loading} className={classes.headerBtn}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton edge="end" aria-label="New Evaluation" onClick={() => history.push(RouteConstants.ADMIN_PROSPECT_EVALUATION_HOME)} className={classes.headerBtn}>
            <AddIcon />NEW EVALUATION
          </IconButton>
          <IconButton edge="end" aria-label="Back to Search" onClick={() => history.push(RouteConstants.ADMIN_EVALUATION_SEARCH)} className={classes.headerBtn}>
            <SearchIcon />SEARCH
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box p={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
        <Typography variant="body2" className={classes.intro}>
          These prospects were added while offline or as a quick entry while evaluating. Map each one to an existing official profile to
          transfer its evaluations. When an email match is found it is suggested automatically; otherwise,
          search by name to select the correct profile. Matched profiles show the official&apos;s head-shot.
        </Typography>
        {/* Clear search */}
        {!loading && prospects.length > 0 && (
          <TextField
            size="small"
            variant="outlined"
            fullWidth
            placeholder="Search prospects by name or email"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); setSelectedDetail(null); }}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" style={{ color: '#777', marginRight: 8 }} />,
              endAdornment: search ? (
                <Tooltip title="Clear search">
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={() => { setSearch(''); setPage(1); setSelectedDetail(null); }}
                  >
                    <ClearIcon fontSize="small" style={{ color: '#777' }} />
                  </IconButton>
                </Tooltip>
              ) : null,
            }}
            style={{ backgroundColor: '#fff', marginBottom: 16, maxWidth: 480 }} 
          />
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={8}>
            <CircularProgress />
          </Box>
        ) : prospects.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography variant="h6" gutterBottom>No prospects awaiting mapping</Typography>
            <Typography variant="body2">
              There are no prospects with evaluations to map.
            </Typography>
          </Box>
        ) : filteredProspects.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography variant="h6" gutterBottom>No matching prospects</Typography>
            <Typography variant="body2">
              No prospects match &quot;{search.trim()}&quot;. Try a different name or email.
            </Typography>
          </Box>
        ) : (
          pagedProspects.map((prospect) => {
            const uid = prospect.evalProspectUID;
            const state = matchState[uid] || {};
            const hasEmailMatch = !!prospect.emailMatchOfficialUID;
            return (
              <Box key={uid} className={classes.prospectCard}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} style={{ gap: 8 }}>
                  <span className={classes.prospectBadge}>Prospect</span>
                  <Chip size="small" className={classes.evalChip} label={`${prospect.evaluationCount || 0} evaluation(s)`} />
                </Box>

                <Box className={classes.searchInputs}>
                  <TextField
                    size="small"
                    variant="outlined"
                    label="First name"
                    className={classes.nameField}
                    value={getFirst(prospect)}
                    onChange={(e) => updateMatchState(uid, { firstName: e.target.value })}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearchOfficials(prospect)}
                  />
                  <TextField
                    size="small"
                    variant="outlined"
                    label="Last name"
                    className={classes.nameField}
                    value={getLast(prospect)}
                    onChange={(e) => updateMatchState(uid, { lastName: e.target.value })}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearchOfficials(prospect)}
                  />
                  {prospect.email && (
                    <TextField
                      size="small"
                      variant="outlined"
                      label="Email"
                      className={classes.emailField}
                      value={prospect.email}
                      InputProps={{ readOnly: true }}
                    />
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={state.searching ? <CircularProgress size={14} style={{ color: '#fff' }} /> : <SearchIcon />}
                    onClick={() => handleSearchOfficials(prospect)}
                    disabled={state.searching}
                    style={{ textTransform: 'none' }}
                  >
                    Search
                  </Button>
                </Box>

                {hasEmailMatch && (
                  <Box className={classes.suggestion}>
                    <Box display="flex" alignItems="center" gridGap={8}>
                      <EmailIcon fontSize="small" style={{ color: '#059669' }} />
                      <Typography variant="body2">
                        Email match: <strong>{prospect.emailMatchName || 'Official'}</strong>
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<LinkIcon />}
                      style={{ backgroundColor: '#059669', color: '#fff', textTransform: 'none' }}
                      onClick={() => requestMap(prospect, {
                        officialUID: prospect.emailMatchOfficialUID,
                        firstName: prospect.firstName,
                        lastName: prospect.lastName,
                      })}
                    >
                      Map
                    </Button>
                  </Box>
                )}

                {state.searched && (
                  <Box className={classes.matchArea}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Typography className={classes.matchAreaTitle}>
                        Match to an existing official
                      </Typography>
                      <Tooltip title="Clear search results">
                        <IconButton
                          size="small"
                          aria-label="Clear search results"
                          onClick={() => handleClearSearch(prospect)}
                          disabled={state.searching}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {(state.results || []).length === 0 && (
                      <Typography variant="body2" style={{ color: '#999', padding: '4px 0' }}>
                        No matching officials found.
                      </Typography>
                    )}

                    {(state.results || []).length > 0 && (
                      <>
                        <Typography variant="caption" style={{ color: '#0f766e', fontWeight: 600, display: 'block', margin: '4px 0' }}>
                          {(state.results || []).length} matching official(s) found. Scroll to see more.
                        </Typography>
                        <Box className={classes.resultsList}>
                          {(state.results || []).map((official) => {
                            const isSelected = selectedDetail
                              && selectedDetail.official
                              && selectedDetail.official.officialUID === official.officialUID
                              && selectedDetail.prospect
                              && selectedDetail.prospect.evalProspectUID === uid;
                            return (
                            <Box
                              key={official.officialUID}
                              className={`${classes.matchRow}${isSelected ? ` ${classes.matchRowSelected}` : ''}`}
                              onClick={() => handleSelectOfficial(prospect, official)}
                            >
                              <Box className={classes.matchRowInfo}>
                                <Avatar
                                  className={classes.officialAvatar}
                                  src={official.headShot || undefined}
                                  alt={fullName(official.firstName, official.lastName)}
                                >
                                  {getInitials(official.firstName, official.lastName)}
                                </Avatar>
                                <Box minWidth={0}>
                                  <Typography variant="body2" className={classes.officialName}>
                                    {fullName(official.firstName, official.lastName)}
                                    {typeof official.matchScore === 'number' && (
                                      <span
                                        className={classes.matchScoreBadge}
                                        style={getMatchScoreStyle(official.matchScore)}
                                        title="Name match confidence"
                                      >
                                        {official.matchScore}% match
                                      </span>
                                    )}
                                  </Typography>
                                  <Typography variant="caption" style={{ color: '#777' }}>
                                    {official.email || 'No email'}{official.userTags ? ` • ${official.userTags}` : ''}
                                  </Typography>
                                </Box>
                              </Box>
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<LinkIcon />}
                                onClick={(e) => { e.stopPropagation(); requestMap(prospect, official); }}
                                style={{ textTransform: 'none', backgroundColor: '#0f766e', color: '#fff' }}
                              >
                                Map
                              </Button>
                            </Box>
                            );
                          })}
                        </Box>
                      </>
                    )}
                  </Box>
                )}
              </Box>
            );
          })
        )}

        {!loading && filteredProspects.length > PAGE_SIZE && (
          <Box display="flex" justifyContent="center" alignItems="center" pt={2}>
            <Pagination
              count={pageCount}
              page={currentPage}
              onChange={(e, value) => setPage(value)}
              color="primary"
              size="small"
            />
          </Box>
        )}
          </Grid>

          <Grid item xs={12} md={5}>
            <Box className={classes.rightPanel}>
              <div className={classes.rightPanelHeader}>Official Details</div>
              {selectedDetail && selectedOfficial ? (
                <Box className={classes.detailBody}>
                  <Avatar
                    className={classes.detailAvatar}
                    src={selectedOfficial.headShot || undefined}
                    alt={fullName(selectedOfficial.firstName, selectedOfficial.lastName)}
                  >
                    {getInitials(selectedOfficial.firstName, selectedOfficial.lastName)}
                  </Avatar>

                  <Typography className={classes.detailName}>
                    {fullName(selectedOfficial.firstName, selectedOfficial.lastName)}
                  </Typography>

                  {typeof selectedOfficial.matchScore === 'number' && (
                    <span
                      className={classes.matchScoreBadge}
                      style={{ ...getMatchScoreStyle(selectedOfficial.matchScore), marginLeft: 0, marginTop: 6 }}
                      title="Name match confidence"
                    >
                      {selectedOfficial.matchScore}% name match
                    </span>
                  )}

                  <Box className={classes.detailInfoList}>
                    <Box className={classes.detailInfoRow}>
                      <SupervisorAccountIcon fontSize="small" style={{ color: '#0f766e' }} />
                      <span className={classes.detailInfoLabel}>Primary Scout</span>
                      <span className={classes.detailInfoValue}>
                        {(selectedProfile && selectedProfile.primaryScout)
                          || (profileLoading ? 'Loading…' : 'Not assigned')}
                      </span>
                    </Box>
                    <Box className={classes.detailInfoRow}>
                      <PhoneIcon fontSize="small" style={{ color: '#0f766e' }} />
                      <span className={classes.detailInfoLabel}>Phone</span>
                      <span className={classes.detailInfoValue}>
                        {(selectedProfile && selectedProfile.primaryPhone)
                          || (profileLoading ? 'Loading…' : 'Not available')}
                      </span>
                    </Box>
                    <Box className={classes.detailInfoRow}>
                      <EmailIcon fontSize="small" style={{ color: '#0f766e' }} />
                      <span className={classes.detailInfoLabel}>Email</span>
                      <span className={classes.detailInfoValue}>
                        {(selectedProfile && selectedProfile.email)
                          || selectedOfficial.email || 'No email on file'}
                      </span>
                    </Box>
                    <Box className={classes.detailInfoRow}>
                      <GroupWorkIcon fontSize="small" style={{ color: '#0f766e' }} />
                      <span className={classes.detailInfoLabel}>Pool</span>
                      <span className={classes.detailInfoValue}>
                        {(selectedProfile && selectedProfile.poolName)
                          || (profileLoading ? 'Loading…' : 'Not assigned')}
                      </span>
                    </Box>
                  </Box>

                  {selectedOfficial.userTags && (
                    <Box mt={1.5} display="flex" alignItems="center" justifyContent="center" flexWrap="wrap">
                      <LocalOfferIcon fontSize="small" style={{ color: '#1d428a', marginRight: 4 }} />
                      {selectedOfficial.userTags.split(',').map((tag) => tag.trim()).filter(Boolean).map((tag) => (
                        <Chip key={tag} size="small" label={tag} className={classes.detailTagChip} />
                      ))}
                    </Box>
                  )}

                  <Divider style={{ width: '100%', margin: '16px 0' }} />

                  <Typography variant="caption" style={{ color: '#777' }}>
                    Mapping  prospect
                  </Typography>
                  <Typography variant="body2" style={{ fontWeight: 600, color: '#1d428a', marginBottom: 12 }}>
                    {fullName(selectedDetail.prospect.firstName, selectedDetail.prospect.lastName)}
                  </Typography>

                  {(() => {
                    const evaluations = parseEvaluations(selectedDetail.prospect);
                    const count = evaluations.length || (selectedDetail.prospect.evaluationCount || 0);
                    return (
                      <Box className={classes.evalSummaryBox}>
                        <div className={classes.evalSummaryHeading}>
                          <EventNoteIcon fontSize="small" style={{ color: '#0f766e' }} />
                          {count} Evaluation{count !== 1 ? 's' : ''} to transfer
                        </div>
                        {evaluations.length === 0 ? (
                          <Typography variant="caption" style={{ color: '#9aa2ad' }}>
                            Evaluation details are unavailable.
                          </Typography>
                        ) : (
                          evaluations.map((evaluation, index) => (
                            <Box key={evaluation.EvaluationID || index} className={classes.evalSummaryItem}>
                              <div className={classes.evalSummaryEvent}>
                                <span>{evaluation.EventName || 'Unspecified event'}</span>
                                <span
                                  className={`${classes.evalStatusChip} ${evaluation.IsDraft ? classes.evalStatusDraft : classes.evalStatusSubmitted}`}
                                >
                                  {evaluation.IsDraft ? 'Draft' : 'Submitted'}
                                </span>
                              </div>
                              <div className={classes.evalSummaryMeta}>
                                <span>{formatEvalDate(evaluation.EvaluationDate)}</span>
                                {evaluation.GameTypeName ? <span>• {evaluation.GameTypeName}</span> : null}
                              </div>
                            </Box>
                          ))
                        )}
                      </Box>
                    );
                  })()}

                  <Button
                    variant="contained"
                    startIcon={<LinkIcon />}
                    onClick={() => requestMap(selectedDetail.prospect, selectedOfficial)}
                    style={{ textTransform: 'none', backgroundColor: '#0f766e', color: '#fff', marginTop: 12 }}
                  >
                    Map &amp; Transfer Evaluations
                  </Button>
                </Box>
              ) : (
                <Box className={classes.detailPlaceholder}>
                  <PersonOutlineIcon style={{ fontSize: 48, color: '#c7ccd3' }} />
                  <Typography variant="body2">
                    Select a matched official from the list to view their profile and head-shot here.
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <ConfirmationDialog
        open={confirm.open}
        title="Confirm mapping"
        isLoading={mapping}
        txtConfirm="Map & Transfer"
        txtCancel="Cancel"
        handleResponse={handleConfirmMap}
        message={
          confirm.prospect && confirm.official ? (
            <Box>
              <Typography variant="body2" gutterBottom>
                Transfer all evaluations from the prospect
                {' '}<strong>{fullName(confirm.prospect.firstName, confirm.prospect.lastName)}</strong>
                {' '}to the official
                {' '}<strong>{fullName(confirm.official.firstName, confirm.official.lastName)}</strong>?
              </Typography>
              <Typography variant="body2" style={{ color: 'rgba(0, 0, 0, 0.87)' }}>
                The temporary prospect record will be removed. This action cannot be undone.
              </Typography>
            </Box>
          ) : ''
        }
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setToast((t) => ({ ...t, open: false }))} severity={toast.severity}>
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MapOfficialsPage;
